import {useEffect,useState} from 'react';
import {Link,useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {dataClient} from '../data/dataClient';
import {requestOtp,authWithOtp} from '../services/pb';
import {workspacePath} from '../services/paths';

const {FiCommand,FiArrowRight,FiMail,FiKey,FiRefreshCw,FiDownload}=FiIcons;

const OTP_LIFETIME_S=180;

// Shared OTP step component: after requestOTP the user sees the identical
// "check your inbox" state whether or not the account exists (enumeration-safe,
// frontend.md §8.2) — never "user not found".
function OtpStep({email,otpId,onComplete}) {
  const navigate=useNavigate();
  const [code,setCode]=useState('');
  const [error,setError]=useState(null);
  const [busy,setBusy]=useState(false);
  const [countdown,setCountdown]=useState(OTP_LIFETIME_S);

  // Countdown ticks down; on expiry the code is no longer accepted.
  useEffect(()=> {
    const timer=setInterval(()=> {
      setCountdown((value)=> {
        if (value<=1) clearInterval(timer);
        return Math.max(0,value-1);
      });
    },1000);
    return ()=> clearInterval(timer);
  },[]);

  async function submit(event) {
    event.preventDefault();
    if (code.trim().length<8 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await authWithOtp(otpId,code);
      await dataClient.initSession();
      onComplete?.(navigate);
    } catch (err) {
      // Wrong code / expired / rate-limited all land here with a plain message.
      setError('That code didn’t match or has expired. Check the email and try again, or request a new code.');
    } finally {
      setBusy(false);
    }
  }

  const expired=countdown===0;

  return (
    <>
      <div className="otp-sent">
        <SafeIcon icon={FiMail} />
        <p>We sent an 8-character code to <b>{email}</b>. It expires in {countdown}s.</p>
      </div>
      <form onSubmit={submit}>
        <label>One-time code
          <input
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={8}
            value={code}
            onChange={(event)=> setCode(event.target.value)}
            placeholder="••••••••"
            disabled={expired}
          />
        </label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="button primary" disabled={busy || code.trim().length<8 || expired}>
          <SafeIcon icon={FiKey} />
          {busy ? 'Verifying…' : 'Verify & continue'}
        </button>
        {expired && (
          <button className="button ghost" type="button" onClick={()=> window.location.reload()}>
            <SafeIcon icon={FiRefreshCw} />
            Request a new code
          </button>
        )}
      </form>
    </>
  );
}

export function SignIn({installPrompt,onInstall}) {
  const navigate=useNavigate();
  const [email,setEmail]=useState('');
  const [step,setStep]=useState('email'); // 'email' | 'otp'
  const [otpId,setOtpId]=useState(null);
  const [error,setError]=useState(null);
  const [busy,setBusy]=useState(false);

  async function sendCode(event) {
    event.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      // Enumeration-safe: same otpId-shaped response for unknown emails — the
      // UI never reveals whether the account exists (frontend.md §8.2).
      const id=await requestOtp(email.trim());
      setOtpId(id);
      setStep('otp');
    } catch (err) {
      setError('Could not request a code. Check the address and try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page"><section className="auth-card"><span className="auth-logo"><SafeIcon icon={FiCommand} /></span><span className="eyebrow">Business Command Center</span><h1>Welcome back.</h1><p>Passwordless sign-in — we email you a one-time code.</p>
      {step==='email' ? (
        <form onSubmit={sendCode}>
          <label>Work email
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event)=> setEmail(event.target.value)}
              placeholder="you@yourbusiness.com"
            />
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="button primary" disabled={busy || !email.trim()}>
            <SafeIcon icon={FiMail} />
            {busy ? 'Sending…' : 'Send me a code'}
          </button>
        </form>
      ) : (
        <OtpStep email={email} otpId={otpId} onComplete={(go)=> {
          const snapshot=dataClient.peek();
          go(snapshot.session?.authenticated ? workspacePath('headquarters',snapshot) : '/signin');
        }} />
      )}
      {installPrompt && (
        <button type="button" className="button ghost" onClick={onInstall}>
          <SafeIcon icon={FiDownload} />
          Install app
        </button>
      )}
      <small>New here? <Link to="/signup">Create your workspace</Link></small>
    </section></main>
  );
}

export function Signup({installPrompt,onInstall}) {
  const navigate=useNavigate();
  const [step,setStep]=useState(1); // 1 account+workspace → 2 OTP verify
  const [email,setEmail]=useState('');
  const [businessName,setBusinessName]=useState('');
  const [otpId,setOtpId]=useState(null);
  const [error,setError]=useState(null);
  const [busy,setBusy]=useState(false);

  async function provision(event) {
    event.preventDefault();
    if (!email.trim() || !businessName.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      // Server-mediated signup (backend.md §6.1): creates the user (random
      // password — OTP is the auth method), business, owner membership, brand
      // profile and the seeded roster. No token is issued here.
      const response=await fetch('/api/auth/signup',{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({email:email.trim(),businessName:businessName.trim(),planSlug:'free'})
      });
      const body=await response.json().catch(()=> ({}));
      if (!response.ok) {
        const message=body?.error?.message
          || (response.status===409 ? 'An account with this email already exists — sign in instead.' : 'Signup failed; please retry.');
        throw new Error(message);
      }
      // Step 2 — run the same OTP pair to complete login (§8.3).
      const id=await requestOtp(email.trim());
      setOtpId(id);
      setStep(2);
    } catch (err) {
      setError(err?.message || 'Signup failed; please retry.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page"><section className="auth-card"><span className="eyebrow">Step {step} of 2</span><h1>{step===1 ? 'Create your workspace' : 'Verify your email'}</h1><div className="progress"><span style={{width: `${step * 50}%`}} /></div>
      {step===1 && (
        <form onSubmit={provision}>
          <label>Work email
            <input type="email" required autoComplete="email" value={email} onChange={(event)=> setEmail(event.target.value)} placeholder="you@yourbusiness.com" />
          </label>
          <label>Business name
            <input required value={businessName} onChange={(event)=> setBusinessName(event.target.value)} placeholder="Northstar Studio" />
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="button primary" disabled={busy || !email.trim() || !businessName.trim()}>
            <SafeIcon icon={FiArrowRight} />
            {busy ? 'Creating…' : 'Create workspace'}
          </button>
          <small>Your team of specialist bots is provisioned for you. No password needed — you’ll verify by email next.</small>
        </form>
      )}
      {step===2 && <OtpStep email={email} otpId={otpId} onComplete={(go)=> {
        const snapshot=dataClient.peek();
        go(snapshot.session?.authenticated ? workspacePath('introduction',snapshot) : '/signin');
      }} />}
      {installPrompt && (
        <button type="button" className="button ghost" onClick={onInstall}>
          <SafeIcon icon={FiDownload} />
          Install app
        </button>
      )}
      <small><Link to="/signin">Back to sign in</Link></small>
    </section></main>
  );
}
